import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Store,
  Search,
  ShoppingCart,
  IndianRupee,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  SlidersHorizontal,
  User,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { shopAPI } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';

const PRODUCTS_PER_PAGE = 12;

export default function Storefront() {
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, getItemCount, cart, updateQuantity } = useCart();

  const categoryScrollRef = useRef(null);
  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  const debounceRef = useRef(null);
  const legacyProductsRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);

  const [useLegacyFallback, setUseLegacyFallback] = useState(false);

  useEffect(() => {
    legacyProductsRef.current = null;
    setUseLegacyFallback(false);
    setProducts([]);
    setPage(1);
    setHasMoreProducts(false);
    setTotalProducts(0);

    fetchShopMeta();

    if (shopSlug) {
      localStorage.setItem('lastVisitedStore', shopSlug);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSlug]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!shopSlug) return;

    setPage(1);
    fetchProducts({
      pageNumber: 1,
      append: false,
      searchValue: debouncedSearch,
      categoryValue: selectedCategory,
      sortValue: sortBy
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSlug, debouncedSearch, selectedCategory, sortBy]);

  const fetchShopMeta = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await shopAPI.getShop(shopSlug);
      setShop(response.data?.seller || null);
      setCategories(response.data?.categories || []);
    } catch (err) {
      console.error('Failed to fetch shop:', err);
      setError('Shop not found');
    } finally {
      setLoading(false);
    }
  };

  const applyClientSideFilterSortPaginate = useCallback(
    (sourceProducts, { pageNumber = 1, searchValue = '', categoryValue = 'all', sortValue = 'newest' } = {}) => {
      const normalizedSearch = String(searchValue || '').trim().toLowerCase();

      let filtered = Array.isArray(sourceProducts) ? [...sourceProducts] : [];

      filtered = filtered.filter((product) => {
        const productName = String(product?.name || '').toLowerCase();
        const productDesc = String(product?.description || '').toLowerCase();

        const matchesSearch =
          !normalizedSearch ||
          productName.includes(normalizedSearch) ||
          productDesc.includes(normalizedSearch);

        const matchesCategory =
          categoryValue === 'all' ||
          String(product?.category_id) === String(categoryValue);

        return matchesSearch && matchesCategory;
      });

      filtered.sort((a, b) => {
        if (sortValue === 'price_low') {
          return Number(a?.final_price || 0) - Number(b?.final_price || 0);
        }
        if (sortValue === 'price_high') {
          return Number(b?.final_price || 0) - Number(a?.final_price || 0);
        }
        return new Date(b?.created_at || 0) - new Date(a?.created_at || 0);
      });

      const totalItems = filtered.length;
      const totalPages = totalItems > 0 ? Math.ceil(totalItems / PRODUCTS_PER_PAGE) : 0;
      const startIndex = (pageNumber - 1) * PRODUCTS_PER_PAGE;
      const endIndex = startIndex + PRODUCTS_PER_PAGE;
      const pageItems = filtered.slice(startIndex, endIndex);

      return {
        items: pageItems,
        pagination: {
          page: pageNumber,
          total_items: totalItems,
          total_pages: totalPages,
          has_next: pageNumber < totalPages
        }
      };
    },
    []
  );

  const fetchLegacyProducts = useCallback(
    async ({ pageNumber = 1, append = false, searchValue = '', categoryValue = 'all', sortValue = 'newest' } = {}) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setProductsLoading(true);
        }

        if (!legacyProductsRef.current) {
          const fallbackResponse = await shopAPI.getShop(shopSlug, { include_products: true });
          legacyProductsRef.current = fallbackResponse.data?.products || [];

          // In case shop/categories were not already loaded properly
          if (!shop && fallbackResponse.data?.seller) {
            setShop(fallbackResponse.data.seller);
          }
          if ((!categories || categories.length === 0) && fallbackResponse.data?.categories) {
            setCategories(fallbackResponse.data.categories);
          }
        }

        const result = applyClientSideFilterSortPaginate(legacyProductsRef.current, {
          pageNumber,
          searchValue,
          categoryValue,
          sortValue
        });

        const items = result.items || [];
        const pagination = result.pagination || {};

        setProducts((prev) => (append ? [...prev, ...items] : items));
        setPage(pagination.page || pageNumber);
        setHasMoreProducts(Boolean(pagination.has_next));
        setTotalProducts(Number(pagination.total_items || 0));
      } catch (err) {
        console.error('Failed to fetch fallback products:', err);
        if (!append) {
          setProducts([]);
          setHasMoreProducts(false);
          setTotalProducts(0);
        }
      } finally {
        setProductsLoading(false);
        setLoadingMore(false);
      }
    },
    [shopSlug, applyClientSideFilterSortPaginate, shop, categories]
  );

  const fetchProducts = useCallback(
    async ({
      pageNumber = 1,
      append = false,
      searchValue = '',
      categoryValue = 'all',
      sortValue = 'newest'
    } = {}) => {
      if (useLegacyFallback) {
        await fetchLegacyProducts({
          pageNumber,
          append,
          searchValue,
          categoryValue,
          sortValue
        });
        return;
      }

      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setProductsLoading(true);
        }

        const params = {
          page: pageNumber,
          limit: PRODUCTS_PER_PAGE,
          sort_by: sortValue
        };

        if (searchValue) {
          params.search = searchValue;
        }

        if (categoryValue && categoryValue !== 'all') {
          params.category_id = categoryValue;
        }

        const response = await shopAPI.getShopProducts(shopSlug, params);
        const items = response.data?.items || [];
        const pagination = response.data?.pagination || {};

        setProducts((prev) => (append ? [...prev, ...items] : items));
        setPage(pagination.page || pageNumber);
        setHasMoreProducts(Boolean(pagination.has_next));
        setTotalProducts(Number(pagination.total_items || 0));
      } catch (err) {
        const status = err?.response?.status;

        // If new paginated endpoint is not live yet, fallback automatically
        if (status === 404) {
          console.warn('Paginated products API not available. Falling back to legacy shop products response.');
          setUseLegacyFallback(true);

          await fetchLegacyProducts({
            pageNumber,
            append,
            searchValue,
            categoryValue,
            sortValue
          });
          return;
        }

        console.error('Failed to fetch products:', err);
        if (!append) {
          setProducts([]);
          setHasMoreProducts(false);
          setTotalProducts(0);
        }
      } finally {
        setProductsLoading(false);
        setLoadingMore(false);
      }
    },
    [shopSlug, useLegacyFallback, fetchLegacyProducts]
  );

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!loadMoreRef.current || !hasMoreProducts || productsLoading || loadingMore) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting) {
          fetchProducts({
            pageNumber: page + 1,
            append: true,
            searchValue: debouncedSearch,
            categoryValue: selectedCategory,
            sortValue: sortBy
          });
        }
      },
      {
        root: null,
        rootMargin: '250px',
        threshold: 0.1
      }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    hasMoreProducts,
    productsLoading,
    loadingMore,
    page,
    debouncedSearch,
    selectedCategory,
    sortBy,
    fetchProducts
  ]);

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getCartQuantity = (productId) => {
    const item = cart.find((i) => i.id === productId);
    return item ? item.quantity : 0;
  };

  const handleAddToCart = (product, e) => {
    if (e) e.preventDefault();
    addToCart(product, 1, shopSlug);
    toast.success(`${product.name} added to cart`);
  };

  const handleQuantityChange = (product, delta) => {
    const currentQty = getCartQuantity(product.id);
    const newQty = currentQty + delta;

    if (newQty <= 0) {
      updateQuantity(product.id, 0);
    } else {
      if (currentQty === 0) {
        addToCart(product, 1, shopSlug);
      } else {
        updateQuantity(product.id, newQty);
      }
    }
  };

  const handleBuyNow = (product) => {
    addToCart(product, 1, shopSlug);
    navigate(`/shop/${shopSlug}/checkout`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <Store className="h-16 w-16 text-muted-foreground mb-4" />
        <h1 className="font-heading text-2xl font-bold mb-2">Shop Not Found</h1>
        <p className="text-muted-foreground mb-4">This shop doesn't exist or has been deactivated.</p>
        <Link to="/">
          <Button>Go Home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="storefront-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Store className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg leading-tight">{shop?.business_name}</h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {user && user.role === 'customer' ? (
                <Link to={`/customer/orders?store=${shopSlug}`}>
                  <Button variant="ghost" size="icon" data-testid="orders-btn">
                    <Package className="h-5 w-5" />
                  </Button>
                </Link>
              ) : (
                <Link to={`/customer/login?redirect=/shop/${shopSlug}`}>
                  <Button variant="ghost" size="icon" data-testid="login-btn">
                    <User className="h-5 w-5" />
                  </Button>
                </Link>
              )}

              <Link to={`/shop/${shopSlug}/cart`}>
                <Button variant="outline" className="relative" data-testid="cart-btn">
                  <ShoppingCart className="h-5 w-5" />
                  {getItemCount() > 0 && (
                    <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                      {getItemCount()}
                    </span>
                  )}
                </Button>
              </Link>
            </div>
          </div>

          {/* Search */}
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Categories Slider */}
        <div className="relative border-t border-border bg-muted/30">
          <button
            onClick={() => scrollCategories('left')}
            className="absolute left-0 top-0 bottom-0 z-10 px-2 bg-gradient-to-r from-background to-transparent hidden md:flex items-center"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <div
            ref={categoryScrollRef}
            className="flex gap-3 overflow-x-auto py-3 px-4 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <button
              onClick={() => setSelectedCategory('all')}
              className={`flex-shrink-0 flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                selectedCategory === 'all'
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-card hover:bg-muted border border-border'
              }`}
              data-testid="category-all"
            >
              <div
                className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                  selectedCategory === 'all' ? 'bg-primary-foreground/20' : 'bg-muted'
                }`}
              >
                <SlidersHorizontal className="h-6 w-6" />
              </div>
              <span className="text-xs font-medium whitespace-nowrap">All</span>
            </button>

            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(String(category.id))}
                className={`flex-shrink-0 flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                  selectedCategory === String(category.id)
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-card hover:bg-muted border border-border'
                }`}
                data-testid={`category-${category.id}`}
              >
                <div
                  className={`h-12 w-12 rounded-lg overflow-hidden ${
                    selectedCategory === String(category.id) ? 'ring-2 ring-primary-foreground' : ''
                  }`}
                >
                  {category.image_url ? (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className={`w-full h-full flex items-center justify-center ${
                        selectedCategory === String(category.id) ? 'bg-primary-foreground/20' : 'bg-muted'
                      }`}
                    >
                      <ImageIcon className="h-6 w-6 opacity-50" />
                    </div>
                  )}
                </div>
                <span className="text-xs font-medium whitespace-nowrap max-w-16 truncate">
                  {category.name}
                </span>
              </button>
            ))}
          </div>

          <button
            onClick={() => scrollCategories('right')}
            className="absolute right-0 top-0 bottom-0 z-10 px-2 bg-gradient-to-l from-background to-transparent hidden md:flex items-center"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Sort Controls */}
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {productsLoading
            ? 'Loading products...'
            : `${totalProducts} product${totalProducts !== 1 ? 's' : ''}`}
        </p>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-40" data-testid="sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="price_low">Price: Low → High</SelectItem>
            <SelectItem value="price_high">Price: High → Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Products Grid */}
      <main className="container mx-auto px-4">
        {productsLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <Card key={index} className="border-border/50 overflow-hidden animate-pulse">
                <div className="aspect-square bg-muted" />
                <CardContent className="p-3">
                  <div className="h-3 bg-muted rounded w-20 mb-2" />
                  <div className="h-4 bg-muted rounded w-full mb-2" />
                  <div className="h-4 bg-muted rounded w-24 mb-3" />
                  <div className="h-9 bg-muted rounded-full w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-semibold mb-2">No products found</h3>
            <p className="text-muted-foreground">
              {debouncedSearch ? 'Try a different search term' : 'This category is empty'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {products.map((product) => {
                const cartQty = getCartQuantity(product.id);

                return (
                  <Card
                    key={product.id}
                    className="border-border/50 overflow-hidden group"
                    data-testid={`product-card-${product.id}`}
                  >
                    <Link to={`/shop/${shopSlug}/product/${product.id}`}>
                      <div className="aspect-square bg-muted relative overflow-hidden">
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                          </div>
                        )}

                        {product.discount > 0 && (
                          <Badge className="absolute top-2 left-2 bg-destructive text-xs">
                            -₹{product.discount}
                          </Badge>
                        )}
                      </div>
                    </Link>

                    <CardContent className="p-3">
                      <Link to={`/shop/${shopSlug}/product/${product.id}`}>
                        <p className="text-xs text-muted-foreground">{product.category_name}</p>
                        <h3 className="font-medium line-clamp-2 mt-1 text-sm hover:text-primary transition-colors">
                          {product.name}
                        </h3>
                      </Link>

                      <div className="flex items-baseline gap-1 mt-2">
                        <span className="font-heading font-bold flex items-center">
                          <IndianRupee className="h-3.5 w-3.5" />
                          {product.final_price}
                        </span>
                        {product.discount > 0 && (
                          <span className="text-xs text-muted-foreground line-through">
                            ₹{product.price}
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2 mt-3">
                        {cartQty > 0 ? (
                          <div className="flex-1 flex items-center justify-between bg-muted rounded-full px-2 py-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 rounded-full"
                              onClick={() => handleQuantityChange(product, -1)}
                              data-testid={`decrease-${product.id}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="font-medium text-sm">{cartQty}</span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 rounded-full"
                              onClick={() => handleQuantityChange(product, 1)}
                              data-testid={`increase-${product.id}`}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 rounded-full text-xs"
                            onClick={(e) => handleAddToCart(product, e)}
                            data-testid={`add-to-cart-${product.id}`}
                          >
                            <ShoppingCart className="h-3 w-3 mr-1" /> Add
                          </Button>
                        )}

                        <Button
                          size="sm"
                          className="flex-1 rounded-full text-xs"
                          onClick={() => handleBuyNow(product)}
                          data-testid={`buy-now-${product.id}`}
                        >
                          Buy Now
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {loadingMore && (
              <div className="py-6 flex justify-center">
                <div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full" />
              </div>
            )}

            {!loadingMore && hasMoreProducts && (
              <div ref={loadMoreRef} className="h-10" />
            )}
          </>
        )}
      </main>

      {/* Sticky Cart Bar (Mobile) */}
      {getItemCount() > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 md:hidden">
          <Link to={`/shop/${shopSlug}/cart`}>
            <Button className="w-full rounded-full btn-glow gap-2">
              <ShoppingCart className="h-5 w-5" />
              View Cart ({getItemCount()} items)
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}